// 所见即判定 (honest silhouettes) — the drawn-shape truth, single-sourced
// (TECH_SPEC §4bis.23; spec rnd/iterations/002/design.md).
//
// Everything the renderer draws for obstacles and runner *mass* is defined
// here, in FQ, DOM-free — consumed by BOTH `render.ts` (which rasterizes it
// with canvas calls) and `scripts/fairness-probe.ts` (which measures phantom
// margins against it). One source, so the probe can never drift from the
// pixels. The kill boxes are the sim's, byte-exact and read-only: nothing in
// this module ever feeds back into collision.
//
// Coordinate conventions: world FQ, y UP from the ground line (the sim's
// frame). `render.ts` flips y and scales by GRID_PX when drawing. Floats are
// fine here — this is presentation, not simulation state.
//
// The invariants (unit-tested, DR pins):
//   - fair spike silhouette ⊇ spike kill box; max drawn height 1024 (today's
//     apex — wider at the shoulders, never taller); drawn width = kill width.
//   - fair runner mass ⊇ runner hitbox per in-flight pose, and its horizontal
//     extent ≤ hitW/2 + 0.1 grid each side (thin limbs may overshoot; mass
//     may not).
//   - bar kill-band alpha ≥ 0.2 (the probe's "drawn pixel" floor keeps
//     ≥ 0.05 slack under the band's 0.25).
//   - flag-off returns today's triangle + zero shift, bit-identical.

import {
  BAR_BOTTOM,
  BAR_TOP,
  FQ_ONE,
  HITBOX_SLIDE_H,
  HITBOX_SLIDE_W,
  HITBOX_STAND_H,
  HITBOX_STAND_W,
  SPIKE_H,
  type Obstacle,
} from '@beatme/sim';

export type Pose = 'run' | 'jump' | 'slide' | 'dead';

/** Mirrors render.ts GRID_PX / FQ_ONE (44 px per 1024 FQ). Pinned against the
 *  renderer by a unit test, not an import — geometry must stay render-free. */
export const PX_PER_FQ = 44 / FQ_ONE;
export const FQ_PER_PX = FQ_ONE / 44;

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export type Shape =
  | { kind: 'rect'; r: Rect; alpha: number }
  | { kind: 'roundrect'; r: Rect; rad: number; alpha: number }
  | { kind: 'tri'; a: [number, number]; b: [number, number]; c: [number, number]; alpha: number }
  | { kind: 'circle'; cx: number; cy: number; rad: number; alpha: number };

function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1;
}

export function shapeContains(s: Shape, x: number, y: number): boolean {
  switch (s.kind) {
    case 'rect':
      return inRect(s.r, x, y);
    case 'roundrect': {
      if (!inRect(s.r, x, y)) return false;
      const cx = Math.max(s.r.x0 + s.rad, Math.min(s.r.x1 - s.rad, x));
      const cy = Math.max(s.r.y0 + s.rad, Math.min(s.r.y1 - s.rad, y));
      return (x - cx) ** 2 + (y - cy) ** 2 <= s.rad ** 2;
    }
    case 'circle':
      return (x - s.cx) ** 2 + (y - s.cy) ** 2 <= s.rad ** 2;
    case 'tri': {
      const sign = (p: [number, number], q: [number, number]): number =>
        (q[0] - p[0]) * (y - p[1]) - (q[1] - p[1]) * (x - p[0]);
      const d1 = sign(s.a, s.b);
      const d2 = sign(s.b, s.c);
      const d3 = sign(s.c, s.a);
      const neg = d1 < 0 || d2 < 0 || d3 < 0;
      const pos = d1 > 0 || d2 > 0 || d3 > 0;
      return !(neg && pos);
    }
  }
}

export function unionContains(shapes: readonly Shape[], x: number, y: number): boolean {
  return shapes.some((s) => shapeContains(s, x, y));
}

export function shapeBounds(s: Shape): Rect {
  switch (s.kind) {
    case 'rect':
    case 'roundrect':
      return s.r;
    case 'circle':
      return { x0: s.cx - s.rad, y0: s.cy - s.rad, x1: s.cx + s.rad, y1: s.cy + s.rad };
    case 'tri':
      return {
        x0: Math.min(s.a[0], s.b[0], s.c[0]),
        y0: Math.min(s.a[1], s.b[1], s.c[1]),
        x1: Math.max(s.a[0], s.b[0], s.c[0]),
        y1: Math.max(s.a[1], s.b[1], s.c[1]),
      };
  }
}

export function unionBounds(shapes: readonly Shape[]): Rect {
  const bs = shapes.map(shapeBounds);
  return {
    x0: Math.min(...bs.map((b) => b.x0)),
    y0: Math.min(...bs.map((b) => b.y0)),
    x1: Math.max(...bs.map((b) => b.x1)),
    y1: Math.max(...bs.map((b) => b.y1)),
  };
}

const offset = (s: Shape, dx: number, dy: number): Shape => {
  switch (s.kind) {
    case 'rect':
      return { ...s, r: { x0: s.r.x0 + dx, y0: s.r.y0 + dy, x1: s.r.x1 + dx, y1: s.r.y1 + dy } };
    case 'roundrect':
      return { ...s, r: { x0: s.r.x0 + dx, y0: s.r.y0 + dy, x1: s.r.x1 + dx, y1: s.r.y1 + dy } };
    case 'circle':
      return { ...s, cx: s.cx + dx, cy: s.cy + dy };
    case 'tri':
      return {
        ...s,
        a: [s.a[0] + dx, s.a[1] + dy],
        b: [s.b[0] + dx, s.b[1] + dy],
        c: [s.c[0] + dx, s.c[1] + dy],
      };
  }
};

export function offsetShapes(shapes: readonly Shape[], dx: number, dy: number): Shape[] {
  return shapes.map((s) => offset(s, dx, dy));
}

// ---------------------------------------------------------------------------
// Kill boxes (the sim's truth, restated — never fed back)
// ---------------------------------------------------------------------------

export function obstacleKillBox(o: Obstacle): Rect | null {
  if (o.type === 'spike') return { x0: o.x, y0: 0, x1: o.x + o.w, y1: SPIKE_H };
  if (o.type === 'bar') return { x0: o.x, y0: BAR_BOTTOM, x1: o.x + o.w, y1: BAR_TOP };
  return null; // gap: kills by falling, not by a box
}

export function playerKillBox(x: number, y: number, sliding: boolean): Rect {
  const w = sliding ? HITBOX_SLIDE_W : HITBOX_STAND_W;
  const h = sliding ? HITBOX_SLIDE_H : HITBOX_STAND_H;
  return { x0: x, y0: y, x1: x + w, y1: y + h };
}

/** Mirror of `Sim.hits()` (strict inequalities included): which spike/bar box
 *  overlaps this player state? Used by the death-frame flash and the probe —
 *  a `null` means the death was a gap fall and needs no explanation. */
export function collidingObstacle(
  p: { x: number; y: number; sliding: boolean },
  obstacles: readonly Obstacle[],
): Obstacle | null {
  const box = playerKillBox(p.x, p.y, p.sliding);
  for (const o of obstacles) {
    if (o.type === 'gap') continue;
    if (o.x >= box.x1 || p.x >= o.x + o.w) continue;
    if (o.type === 'spike') {
      if (box.y0 < SPIKE_H && box.y1 > 0) return o;
    } else if (box.y0 < BAR_TOP && box.y1 > BAR_BOTTOM) {
      return o;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Obstacle silhouettes (world FQ, y up from the ground line)
// ---------------------------------------------------------------------------

/** Crown teeth (FQ from the obstacle's left edge), bases ON the kill line
 *  y = 896 — extra ink strictly above the kill box, where it is fair-safe.
 *  Center apex keeps today's 1024 peak; outer teeth stop at 1000. */
const SPIKE_TEETH: ReadonlyArray<[[number, number], [number, number], [number, number]]> = [
  [
    [0, 896],
    [136, 1000],
    [273, 896],
  ],
  [
    [273, 896],
    [410, 1024],
    [546, 896],
  ],
  [
    [546, 896],
    [683, 1000],
    [819, 896],
  ],
];

/**
 * The spike's drawn silhouette. Fair: the kill rect exactly, plus a crown of
 * three teeth on top of the kill line (silhouette ⊇ kill box, constructive).
 * Flag-off: today's triangle, apex at 1 grid — bit-identical to the shipped
 * draw.
 */
export function spikeShapes(o: Obstacle, fair: boolean): Shape[] {
  if (!fair) {
    return [
      {
        kind: 'tri',
        a: [o.x, 0],
        b: [o.x + o.w / 2, FQ_ONE],
        c: [o.x + o.w, 0],
        alpha: 1,
      },
    ];
  }
  return [
    { kind: 'rect', r: { x0: o.x, y0: 0, x1: o.x + o.w, y1: SPIKE_H }, alpha: 1 },
    ...SPIKE_TEETH.map(
      (t): Shape => ({
        kind: 'tri',
        a: [o.x + t[0][0], t[0][1]],
        b: [o.x + t[1][0], t[1][1]],
        c: [o.x + t[2][0], t[2][1]],
        alpha: 1,
      }),
    ),
  ];
}

/** Fair-only body texture: two v-notch grooves, 3 px wide, x = 273 & 546 FQ
 *  from the left edge, y 896 → 640 — drawn INSIDE the body rect (darkened
 *  accent), texture, never holes. The coverage invariant samples exactly
 *  these columns to prove the body stays solid under them. */
export const SPIKE_GROOVE_XS: readonly number[] = [273, 546];
export const SPIKE_GROOVE_Y0 = 640;
export const SPIKE_GROOVE_Y1 = 896;

export function spikeGrooves(o: Obstacle): Rect[] {
  const half = (3 / 2) * FQ_PER_PX;
  return SPIKE_GROOVE_XS.map((gx) => ({
    x0: o.x + gx - half,
    y0: SPIKE_GROOVE_Y0,
    x1: o.x + gx + half,
    y1: SPIKE_GROOVE_Y1,
  }));
}

/** The bar was already honest (beam + rail + 0.25-alpha kill band cover the
 *  kill box exactly; the beam's ±14 px overhang is fair-safe over-draw), so
 *  both modes share one shape list. Alphas ride along for the probe's
 *  ≥ 0.2 "drawn pixel" floor. */
export function barShapes(o: Obstacle): Shape[] {
  const overhang = 14 * FQ_PER_PX;
  const beamH = 12 * FQ_PER_PX;
  const railH = 4 * FQ_PER_PX;
  const postHalf = 3 * FQ_PER_PX;
  const cx = o.x + o.w / 2;
  return [
    // post, ground to the beam
    { kind: 'rect', r: { x0: cx - postHalf, y0: 0, x1: cx + postHalf, y1: BAR_BOTTOM }, alpha: 1 },
    // beam (accent), sitting on the kill line
    {
      kind: 'rect',
      r: { x0: o.x - overhang, y0: BAR_BOTTOM, x1: o.x + o.w + overhang, y1: BAR_BOTTOM + beamH },
      alpha: 1,
    },
    // upper rail
    {
      kind: 'rect',
      r: { x0: o.x - overhang, y0: BAR_TOP - railH, x1: o.x + o.w + overhang, y1: BAR_TOP },
      alpha: 1,
    },
    // kill band between them — MUST stay ≥ 0.2 (probe floor, DR-R2 pin)
    {
      kind: 'rect',
      r: {
        x0: o.x - overhang,
        y0: BAR_BOTTOM + beamH,
        x1: o.x + o.w + overhang,
        y1: BAR_TOP - railH,
      },
      alpha: 0.25,
    },
  ];
}

/** Every drawn shape of an obstacle (gap draws nothing — it is a punch-out). */
export function obstacleShapes(o: Obstacle, fair: boolean): Shape[] {
  if (o.type === 'spike') return spikeShapes(o, fair);
  if (o.type === 'bar') return barShapes(o);
  return [];
}

// ---------------------------------------------------------------------------
// Runner: anchor shift + drawn mass
// ---------------------------------------------------------------------------

/**
 * Fair mode translates the sprite at `sx + shift` so the drawn body is
 * centered on the hitbox `[x, x + hitW]` instead of on the trailing edge.
 * Exactly hitW/2 per pose: +307 FQ (stand/jump/run/dead), +409.5 FQ (slide).
 * Flag-off: 0 — today's anchor, bit-identical.
 */
export function runnerShiftFQ(pose: Pose, fair: boolean): number {
  if (!fair) return 0;
  return pose === 'slide' ? HITBOX_SLIDE_W / 2 : HITBOX_STAND_W / 2;
}

const u = FQ_ONE; // 1 grid, for readability below

/** The fair body capsule per body type — the ONE place the live silhouette's
 *  proportions live. The corpse is this same capsule, fallen (see
 *  `runnerCorpse`), so "the runner" and "the runner, down" can never drift
 *  into two different characters again. */
export interface Capsule {
  r: Rect;
  rad: number;
}

const FAIR_CAPSULE: Record<'stand' | 'slide', Capsule> = {
  stand: { r: { x0: -0.36 * u, y0: -0.04 * u, x1: 0.36 * u, y1: 1.86 * u }, rad: 0.14 * u },
  slide: { r: { x0: -0.46 * u, y0: -0.04 * u, x1: 0.46 * u, y1: 0.96 * u }, rad: 0.14 * u },
};

/**
 * The drawn torso+head MASS per pose, relative to the translate origin
 * (fair: hitbox center at foot level; legacy: the trailing-edge anchor).
 * y up. Thin limbs are deliberately absent from the fair mass — they may
 * overshoot (fair-safe) but may not carry coverage.
 *
 * Fair capsules are sized so `runner hitbox ⊆ mass` holds with the corner
 * radius accounted for (rasterized invariant test), while the horizontal
 * extent stays within the ±(hitW/2 + 0.1 u) cap:
 *   run/jump:  x ∈ ±0.36 u (cap 0.40 u), y ∈ [−0.04 u, 1.86 u], r 0.14 u
 *   slide:     x ∈ ±0.46 u (cap 0.50 u), y ∈ [−0.04 u, 0.96 u], r 0.14 u
 *
 * `dead` returns the STANDING capsule so `Pose` stays total for mass callers
 * (the probe's `inFlightPose` never yields `dead` — the margin question is
 * about the body that hit the box, never the corpse). The drawn corpse is
 * `runnerCorpse()`: the same capsule, rotated.
 *
 * Legacy mass mirrors today's stick figure's torso + head. Limbs are excluded
 * on BOTH sides (the pin defines mass as torso+head; thin strokes carry no
 * mass) — the same rule for legacy and fair keeps the two probe modes
 * comparable.
 */
export function runnerMassShapes(pose: Pose, fair: boolean): Shape[] {
  if (fair) {
    const cap = FAIR_CAPSULE[pose === 'slide' ? 'slide' : 'stand'];
    return [{ kind: 'roundrect', r: cap.r, rad: cap.rad, alpha: 1 }];
  }
  // Legacy approximations of render.ts's shipped art (probe --legacy only).
  if (pose === 'slide') {
    return [
      {
        kind: 'roundrect',
        r: { x0: -0.315 * u, y0: 0.252 * u, x1: 0.585 * u, y1: 0.9 * u },
        rad: 0.27 * u,
        alpha: 1,
      },
      { kind: 'circle', cx: 0.378 * u, cy: 0.558 * u, rad: 0.24 * u, alpha: 1 },
    ];
  }
  if (pose === 'dead') {
    // Face-plant: the figure rotated flat; a low slab behind the anchor.
    return [{ kind: 'rect', r: { x0: -0.72 * u, y0: 0, x1: 0.32 * u, y1: 0.46 * u }, alpha: 1 }];
  }
  // run / jump: today's torso roundrect + head circle (limbs are strokes).
  return [
    {
      kind: 'roundrect',
      r: { x0: -0.17 * u, y0: 0.57 * u, x1: 0.19 * u, y1: 1.28 * u },
      rad: 0.16 * u,
      alpha: 1,
    },
    { kind: 'circle', cx: 0.05 * u, cy: 1.56 * u, rad: 0.26 * u, alpha: 1 },
  ];
}

/** The in-flight pose of a (possibly dead) sim state — the death photo shows
 *  a corpse, but the margin question is about the body that hit the box. */
export function inFlightPose(s: { sliding: boolean; grounded: boolean }): Pose {
  if (s.sliding) return 'slide';
  if (!s.grounded) return 'jump';
  return 'run';
}

/** Drawn runner mass in WORLD FQ for a sim state (dead or alive): the mass
 *  shapes at the pose's translate origin. */
export function runnerMassWorld(
  s: { x: number; y: number; sliding: boolean; grounded: boolean },
  fair: boolean,
): Shape[] {
  const pose = inFlightPose(s);
  return offsetShapes(runnerMassShapes(pose, fair), s.x + runnerShiftFQ(pose, fair), s.y);
}

// ---------------------------------------------------------------------------
// The corpse (§4bis.23 art-consistency fix, hardening round H01)
// ---------------------------------------------------------------------------

/**
 * Iteration 002 recentered the LIVE runner onto its hitbox and redrew it as an
 * honest capsule, but kept the shipped stick-figure face-plant for the death
 * pose ("the corpse is post-judgment presentation"). On the preview build the
 * two read as two different characters — a capsule while running, a stick
 * figure on the ground. H01 fixes that by DERIVING the corpse from the live
 * body: the standing capsule, exactly, rotated back onto the ground.
 *
 * What binds the corpse and what does not:
 *  - `runner hitbox ⊆ mass` does NOT bind it. That invariant exists so a death
 *    can never look clear; the corpse is drawn AFTER judgment and the probe
 *    never measures it (`inFlightPose` never returns `dead`).
 *  - The ART does bind it: same capsule, same proportions, same corner radius,
 *    same palette tokens and line weight as the live body (render.ts draws
 *    both through one path).
 *  - The death-frame FLASH binds it: the corpse falls BACKWARD, and
 *    `bounds.x1 ≤ HITBOX_STAND_W / 2` pins that it never reaches past the kill
 *    box's leading edge — so the body can never cover the boxes, the overlap
 *    fill, or the obstacle the flash is explaining.
 *  - It rests ON the ground: `bounds.y0 === 0`.
 */
const DEAD_LEAN = Math.PI / 2 - 0.14;

/** Rotate a point by the fall angle (world FQ, y up; CCW ⇒ head goes −x). */
function fallen(x: number, y: number): [number, number] {
  const c = Math.cos(DEAD_LEAN);
  const s = Math.sin(DEAD_LEAN);
  return [x * c - y * s, x * s + y * c];
}

export interface Corpse {
  /** The live standing capsule, verbatim — rotated and lifted, never redrawn. */
  capsule: Capsule;
  /** Fall angle in WORLD radians (y up, CCW). render.ts negates it for canvas. */
  lean: number;
  /** Post-rotation lift in FQ that drops the fallen body onto the ground. */
  liftFQ: number;
  /** Conservative AABB of the fallen body, origin = hitbox center at foot
   *  level, y up (the corner rounding sits inside the rect, so this is an
   *  envelope — good enough for the two pins above). */
  bounds: Rect;
}

export function runnerCorpse(): Corpse {
  const capsule = FAIR_CAPSULE.stand;
  const { x0, y0, x1, y1 } = capsule.r;
  const pts = [fallen(x0, y0), fallen(x1, y0), fallen(x0, y1), fallen(x1, y1)];
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const liftFQ = -Math.min(...ys);
  return {
    capsule,
    lean: DEAD_LEAN,
    liftFQ,
    bounds: {
      x0: Math.min(...xs),
      y0: Math.min(...ys) + liftFQ,
      x1: Math.max(...xs),
      y1: Math.max(...ys) + liftFQ,
    },
  };
}

// ---------------------------------------------------------------------------
// Camera compensation (§4bis.23; DR-R2 pins)
// ---------------------------------------------------------------------------

/**
 * The forward mass shift eats lookahead; the camera gives it back by moving
 * the anchor LEFT: `anchorPx − FAIR_CAM_COMP_PX[pose]` (subtraction pinned
 * notationally — render.ts applies it exactly in that form). `dead` is the
 * comp of the pose AT death, resolved by the caller (slide-pose spike deaths
 * exist: the slide box is 922 FQ tall vs the spike's 896 kill height).
 */
export const FAIR_CAM_COMP_PX = { stand: 13, jump: 13, slide: 18 } as const;

export type CompPose = keyof typeof FAIR_CAM_COMP_PX;

/** Frames a pose change eases over (linear, matching the pose-art swap). */
export const FAIR_CAM_EASE_FRAMES = 6;

/**
 * Per-render-frame camera comp state. `step(target, settleNow)` returns the
 * comp in px: eased linearly over FAIR_CAM_EASE_FRAMES on a target change,
 * EXCEPT settled instantly when `settleNow` (the death photo — a 5 px camera
 * drift mid-explanation is forbidden, DR-R2 pin).
 */
export class FairCam {
  private value: number = FAIR_CAM_COMP_PX.stand;
  private from: number = FAIR_CAM_COMP_PX.stand;
  private target: CompPose = 'stand';
  private frames = FAIR_CAM_EASE_FRAMES;

  step(target: CompPose, settleNow: boolean): number {
    if (target !== this.target) {
      this.target = target;
      this.from = this.value;
      this.frames = 0;
    }
    const goal = FAIR_CAM_COMP_PX[this.target];
    if (settleNow) {
      this.frames = FAIR_CAM_EASE_FRAMES;
      this.value = goal;
      return this.value;
    }
    if (this.frames < FAIR_CAM_EASE_FRAMES) {
      this.frames++;
      this.value = this.from + ((goal - this.from) * this.frames) / FAIR_CAM_EASE_FRAMES;
    } else {
      this.value = goal;
    }
    return this.value;
  }

  /** Fresh run → steady-state stand comp, no transient on frame 0. */
  reset(): void {
    this.value = FAIR_CAM_COMP_PX.stand;
    this.from = this.value;
    this.target = 'stand';
    this.frames = FAIR_CAM_EASE_FRAMES;
  }
}

// ---------------------------------------------------------------------------
// Phantom margin (the probe's metric; also the regression-lock tests')
// ---------------------------------------------------------------------------

/** Rasterization step: 1 screen px in FQ (≤ 1 px sampling, DR-R2 pin). */
const STEP = FQ_PER_PX;

function rasterBoundary(shapes: readonly Shape[], minAlpha: number): Array<[number, number]> {
  const drawn = shapes.filter((s) => s.alpha >= minAlpha);
  if (drawn.length === 0) return [];
  const b = unionBounds(drawn);
  const nx = Math.max(2, Math.ceil((b.x1 - b.x0) / STEP) + 1);
  const ny = Math.max(2, Math.ceil((b.y1 - b.y0) / STEP) + 1);
  const inside: boolean[] = new Array(nx * ny);
  for (let iy = 0; iy < ny; iy++) {
    const y = b.y0 + iy * STEP;
    for (let ix = 0; ix < nx; ix++) {
      inside[iy * nx + ix] = unionContains(drawn, b.x0 + ix * STEP, y);
    }
  }
  const pts: Array<[number, number]> = [];
  for (let iy = 0; iy < ny; iy++) {
    for (let ix = 0; ix < nx; ix++) {
      if (!inside[iy * nx + ix]) continue;
      const edge =
        ix === 0 ||
        iy === 0 ||
        ix === nx - 1 ||
        iy === ny - 1 ||
        !inside[iy * nx + ix - 1] ||
        !inside[iy * nx + ix + 1] ||
        !inside[(iy - 1) * nx + ix] ||
        !inside[(iy + 1) * nx + ix];
      if (edge) pts.push([b.x0 + ix * STEP, b.y0 + iy * STEP]);
    }
  }
  return pts;
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x0 <= b.x1 && b.x0 <= a.x1 && a.y0 <= b.y1 && b.y0 <= a.y1;
}

/** Alpha at or above which a drawn pixel counts for the margin (the bar's
 *  0.25 kill band qualifies with slack — the floor is tested). */
export const MARGIN_MIN_ALPHA = 0.2;

/**
 * Phantom margin in SCREEN px at a death frame: the min Euclidean distance
 * between the drawn player mass (in-flight pose) and the nearest drawn
 * obstacle pixel (alpha ≥ 0.2); 0 when they intersect. Sampled at ≤ 1 px.
 * `obstacles` should be the handful near the death — every drawn shape of
 * every one of them competes to be "nearest".
 */
export function phantomMarginPx(
  s: { x: number; y: number; sliding: boolean; grounded: boolean },
  obstacles: readonly Obstacle[],
  fair: boolean,
): number {
  const mass = runnerMassWorld(s, fair);
  const mb = unionBounds(mass);
  const pa = rasterBoundary(mass, 0);

  // Per obstacle (their unions can span the whole screen; one raster each
  // keeps the grids small), min over all of them.
  let best = Number.POSITIVE_INFINITY;
  for (const o of obstacles) {
    const obs = obstacleShapes(o, fair).filter((sh) => sh.alpha >= MARGIN_MIN_ALPHA);
    if (obs.length === 0) continue;

    // Intersection: sample the overlap of the two bounding boxes at 1 px.
    const ob = unionBounds(obs);
    if (rectsOverlap(mb, ob)) {
      const x0 = Math.max(mb.x0, ob.x0);
      const x1 = Math.min(mb.x1, ob.x1);
      const y0 = Math.max(mb.y0, ob.y0);
      const y1 = Math.min(mb.y1, ob.y1);
      for (let y = y0; y <= y1 + 1e-9; y += STEP) {
        for (let x = x0; x <= x1 + 1e-9; x += STEP) {
          if (unionContains(mass, x, y) && unionContains(obs, x, y)) return 0;
        }
      }
    }

    const pb = rasterBoundary(obs, MARGIN_MIN_ALPHA);
    for (const [ax, ay] of pa) {
      for (const [bx, by] of pb) {
        const d = (ax - bx) ** 2 + (ay - by) ** 2;
        if (d < best) best = d;
      }
    }
  }
  return Math.sqrt(best) * PX_PER_FQ;
}
